/**
 * Typed Backoffice API client helpers.
 */
import { adminApi } from '@cevop/api-client';

export type OrgSummary = {
  id: string;
  name: string;
  status: string;
  plan: string | null;
  subscriptionStatus: string | null;
  memberCount: number;
  locationCount: number;
  createdAt: string;
  suspendedAt: string | null;
};

export type OrgMember = {
  id: string;
  userId: string;
  displayName: string;
  email: string;
  userStatus: string;
  membershipStatus: string;
  isCanonicalOwner?: boolean;
  roleKeys?: string[];
  roles: string[];
  lastLoginAt: string | null;
  joinedAt: string | null;
};

export type OwnershipHealth = {
  status: 'HEALTHY' | 'UNRESOLVED';
  code: string;
  message: string;
  owner: {
    membershipId: string;
    membershipStatus: string;
    userId: string;
    displayName: string;
    email: string;
    userStatus: string;
    roleKeys: string[];
    roles: string[];
  } | null;
};

export type OrgLocation = {
  id: string;
  name: string;
  status: string;
  createdAt: string;
};

export type OrgIntegration = {
  id: string;
  providerKey: string;
  status: string;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
};

export type OrgSubscription = {
  id: string;
  planKey: string;
  planName: string;
  status: string;
  currencyCode: string;
  amount: string;
  interval: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  pendingPlanKey: string | null;
  pendingPlanName: string | null;
  entitlementCount: number;
};

export type OrgDetail = {
  id: string;
  name: string;
  slug: string;
  status: string;
  plan: string | null;
  subscriptionStatus: string | null;
  memberCount: number;
  locationCount: number;
  createdAt: string;
  suspendedAt: string | null;
  archivedAt: string | null;
  ownerMembershipId: string | null;
  ownership: OwnershipHealth;
  subscription: OrgSubscription | null;
  members: OrgMember[];
  locations: OrgLocation[];
  integrations: OrgIntegration[];
};

export type ActivityEvent = {
  id: string;
  source: 'backoffice' | 'platform';
  action: string;
  outcome: string;
  actor: string;
  memo: string | null;
  occurredAt: string;
};

export type UserSummary = {
  id: string;
  email: string;
  displayName: string;
  emailVerified: boolean;
  status: string;
  createdAt: string;
  lastLoginAt: string | null;
  organizations: Array<{ id: string; name: string; roles: string[] }>;
};

export type AuditRecord = {
  id: string;
  action: string;
  administratorId: string | null;
  organizationId: string | null;
  organization?: { id: string; name: string } | null;
  administrator?: { id: string; displayName: string; email: string } | null;
  targetKind: string | null;
  targetId: string | null;
  memo: string | null;
  outcome: string;
  createdAt: string;
};

export type AuditRecordDetail = AuditRecord & {
  before: unknown;
  after: unknown;
  ipAddress: string | null;
  userAgent: string | null;
  correlationId: string | null;
};

export type TeamMember = {
  id: string;
  email: string;
  displayName: string;
  status: string;
  role: string;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
};

export type OrgHealth = {
  organizationId: string;
  lastMemberLoginAt: string | null;
  lastSaleAt: string | null;
  activeMemberCount: number;
  openTillSessionCount: number;
  activeSessionCount: number;
};

export type AdminSession = {
  id: string;
  isCurrent: boolean;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  userAgent: string | null;
};

export type Announcement = {
  id: string;
  title: string;
  body: string;
  status: string;
  tone?: string;
  dismissible: boolean;
  targetAudience?: string;
  targetPlan?: string | null;
  targetPlanKey?: string | null;
  targetOrganizationId: string | null;
  scheduledAt: string | null;
  startsAt?: string;
  endsAt?: string;
  deactivatedAt: string | null;
  createdAt: string;
};

export type IntegrationConnection = {
  id: string;
  organizationId: string;
  organizationName: string;
  providerKey: string;
  status: string;
  externalAccountName: string | null;
  lastSuccessfulCheckAt: string | null;
  lastFailureAt: string | null;
  lastErrorCode: string | null;
  createdAt: string;
};

export type ImportRecord = {
  id: string;
  organizationId: string;
  organizationName: string;
  type: string;
  filename: string;
  status: string;
  uploadedAt: string;
  committedAt: string | null;
  hasErrors: boolean;
  isWorkbook: boolean;
  sheetCount: number;
};

export type NotificationHealth = {
  total: number;
  unread: number;
  escalated: number;
  byChannel: Record<string, number>;
  recentUndelivered: number;
  recentUnreadInApp?: number;
  emailAttemptsTotal?: number;
  emailFailedLast24h?: number;
  emailByStatus?: Record<string, number>;
};

export type EmailDeliveryRecord = {
  id: string;
  organizationId: string;
  organizationName: string;
  userId: string;
  recipientHint: string | null;
  intentKey: string;
  purpose: string;
  channel: string;
  deliveryId: string;
  attempt: number;
  status: string;
  truthState: string;
  errorCode: string | null;
  retryable: boolean;
  nextAttemptAt: string | null;
  providerMessageId: string | null;
  providerStatus: string | null;
  providerStatusAt: string | null;
  createdAt: string;
  correlationId: string | null;
  resourceHint: string | null;
  safeToRequeue: boolean;
  requeueBlockReason: string | null;
};

export type WebhookDeliveryRecord = {
  id: string;
  organizationId: string;
  organizationName: string;
  endpointName: string;
  endpointKind: string;
  endpointStatus: string;
  endpointUrlHost: string | null;
  eventType: string;
  eventId: string;
  status: string;
  statusCode: number | null;
  error: string | null;
  attempts: number;
  attemptedAt: string;
  failureClass: string;
  terminal: boolean;
  replayAvailable: boolean;
  replayBlockReason: string | null;
  payloadSummary: {
    eventId: string | null;
    eventType: string | null;
    hasData: boolean;
    topLevelKeys: string[];
  } | null;
};

export type BridgeSessionRecord = {
  id: string;
  organizationId: string;
  organizationName: string;
  filename: string;
  status: string;
  progress: number;
  progressLabel: string | null;
  createdAt: string;
  committedAt: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  totalRows: number;
  committedRows: number;
  failedCommitRows: number;
  skippedCommitRows: number;
  validationIssueCount: number;
  reconciliationPassed: boolean | null;
  failureSummary: string | null;
  recoveryHint: string;
};

export type BackofficeFileRecord = {
  id: string;
  organizationId: string;
  organizationName: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  checksumSha256: string;
  purpose: string;
  ownerType: string | null;
  ownerId: string | null;
  createdAt: string;
  status: string;
  deletedAt: string | null;
  attachmentCount: number;
};

export type OperationsHealth = {
  generatedAt: string;
  overallStatus: string;
  readiness?: {
    processReady: boolean;
    cutoverReady: boolean;
    cutoverBlockers: string[];
    note: string;
  };
  asyncWork?: {
    outbox: {
      pending: number;
      processing: number;
      failed: number;
      oldestPendingAgeMs: number | null;
      oldestPendingLabel: string | null;
      alert: string;
    };
    emailFailedLast24h: number;
    webhookExhausted: number;
    webhookFailedLast24h: number;
    bridgeFailedLast24h: number;
    needsAttention: number;
  };
  backupVisibility?: string;
  subsystems: Record<string, { status: string; detail: string }>;
  products: {
    inventory: unknown | { snapshot: unknown; status: string };
    sales: unknown | { snapshot: unknown; status: string };
    accounting: unknown | { snapshot: unknown; status: string };
    procurement: unknown | { snapshot: unknown; status: string };
  };
  jobs: Array<{
    name: string;
    status: string;
    intervalMs: number | null;
    lastExecution: string | null;
    lastSuccess: string | null;
    lastFailure: string | null;
    failureSummary: string | null;
    nextExpectedExecution: string | null;
  }>;
};

export type BillingSubscription = {
  id: string;
  organizationId: string;
  organizationName: string;
  organizationStatus: string;
  planKey: string;
  planName: string;
  status: string;
  currencyCode: string;
  amount: string;
  interval: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelledAt: string | null;
  createdAt: string;
};

export type OrgBilling = {
  organizationId: string;
  organizationName: string;
  primarySubscription: {
    id: string;
    planKey: string;
    planName: string;
    status: string;
    currencyCode: string;
    amount: string;
    interval: string;
    trialEndsAt: string | null;
    currentPeriodEnd: string | null;
    pastDueAt?: string | null;
  } | null;
  graceEndsAt?: string | null;
  subscriptions: Array<{
    id: string;
    planKey: string;
    planName: string;
    status: string;
    amount: string;
    currencyCode: string;
    interval: string;
    createdAt: string;
  }>;
  invoices: Array<{
    id: string;
    number: string;
    status: string;
    currencyCode: string;
    total: string;
    amountPaid: string;
    amountRefunded?: string;
    issuedAt: string | null;
    dueAt: string | null;
    paidAt: string | null;
    createdAt: string;
  }>;
  entitlements: Array<{ id: string; capabilityKey: string; grantedAt: string }>;
  creditNotes?: Array<{
    id: string;
    amount: string;
    remainingAmount?: string;
    currencyCode: string;
    reason: string;
    createdAt: string;
  }>;
  refunds?: Array<{ id: string; invoiceId: string; amount: string; currencyCode: string; reason: string; createdAt: string }>;
  paymentAttempts?: Array<{
    id: string;
    invoiceId: string;
    amount: string;
    currencyCode: string;
    providerKey: string;
    status: string;
    failureCode: string | null;
    failureMessage: string | null;
    initiatedAt: string;
    completedAt: string | null;
  }>;
};

export type OperationalSearchHit = {
  type: string;
  id: string;
  label: string;
  status: string;
  organizationId: string | null;
  organizationName: string | null;
  timestamp: string;
  href: string;
  summary: string;
};

export type SearchResult = {
  organizations: Array<{ id: string; name: string; status: string; memberCount: number; createdAt: string }>;
  users: Array<{
    id: string;
    email: string;
    displayName: string;
    status: string;
    organizations: Array<{ id: string; name: string }>;
  }>;
  operational?: OperationalSearchHit[];
};

function qs(params?: Record<string, string | number | undefined>): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    sp.set(key, String(value));
  }
  const p = sp.toString();
  return p ? `?${p}` : '';
}

export const WEB_APP_URL =
  import.meta.env.VITE_WEB_URL ??
  import.meta.env.VITE_WEB_APP_URL ??
  'http://localhost:5173';

export function impersonationHandoffUrl(impersonationKey: string): string {
  const url = new URL(WEB_APP_URL);
  url.searchParams.set('impersonation_key', impersonationKey);
  return url.toString();
}

export const searchApi = {
  global: (q: string) =>
    adminApi.get<{ data: SearchResult }>(`/admin/v1/search${qs({ q })}`),
};

export const orgsApi = {
  list: (params?: Record<string, string>) =>
    adminApi.get<{ data: { organizations: OrgSummary[]; nextCursor: string | null } }>(`/admin/v1/orgs${qs(params)}`),
  create: (body: { name: string; ownerEmail: string }) =>
    adminApi.post<{ data: { id: string; name: string; slug: string; status: string } }>('/admin/v1/orgs', body),
  detail: (id: string) =>
    adminApi.get<{ data: OrgDetail }>(`/admin/v1/orgs/${id}`),
  health: (id: string) =>
    adminApi.get<{ data: OrgHealth }>(`/admin/v1/orgs/${id}/health`),
  activity: (id: string) =>
    adminApi.get<{ data: { organizationId: string; timeline: ActivityEvent[] } }>(`/admin/v1/orgs/${id}/activity`),
  billing: (id: string) =>
    adminApi.get<{ data: OrgBilling }>(`/admin/v1/orgs/${id}/billing`),
  suspend: (id: string, reason: string) =>
    adminApi.post<{ data: { status: string } }>(`/admin/v1/orgs/${id}/suspend`, { reason }),
  unsuspend: (id: string) =>
    adminApi.post<{ data: { status: string } }>(`/admin/v1/orgs/${id}/unsuspend`, {}),
  archive: (id: string, body: { confirmName: string; reason: string; password: string }) =>
    adminApi.post<{ data: { status: string } }>(`/admin/v1/orgs/${id}/archive`, body),
  exportData: (id: string) =>
    adminApi.get<{ data: unknown }>(`/admin/v1/orgs/${id}/data-export`),
  notes: (id: string) =>
    adminApi.get<{ data: { notes: Array<{ id: string; body: string; author: string; createdAt: string }> } }>(`/admin/v1/orgs/${id}/notes`),
  addNote: (id: string, body: string) =>
    adminApi.post<{ data: { id: string } }>(`/admin/v1/orgs/${id}/notes`, { body }),
  cases: (id: string) =>
    adminApi.get<{ data: { cases: Array<{ id: string; title: string; status: string; ticketRef: string | null; openedBy: string; createdAt: string }> } }>(`/admin/v1/orgs/${id}/cases`),
  createCase: (id: string, body: { title: string; ticketRef?: string; workItemId?: string }) =>
    adminApi.post<{ data: { id: string; title: string } }>(`/admin/v1/orgs/${id}/cases`, body),
  updateCase: (
    caseId: string,
    body: { status?: 'OPEN' | 'WAITING' | 'RESOLVED'; note?: string; ticketRef?: string | null; assigneeAdminId?: string | null },
  ) => adminApi.patch<{ data: { id: string; status: string } }>(`/admin/v1/cases/${caseId}`, body),
  recoverOwnership: (
    id: string,
    body: {
      targetMembershipId: string;
      caseRef: string;
      reason: string;
      password: string;
      confirmOrganizationName: string;
    },
  ) =>
    adminApi.post<{ data: { ownerMembershipId: string; previousOwnerMembershipId: string | null; caseRef: string } }>(
      `/admin/v1/orgs/${id}/ownership/recover`,
      body,
    ),
  listRoles: (id: string) =>
    adminApi.get<{
      data: {
        roles: Array<{
          id: string;
          key: string;
          name: string;
          description: string;
          isSystem: boolean;
          status: string;
          assignableViaAssistance: boolean;
        }>;
      };
    }>(`/admin/v1/orgs/${id}/roles`),
  membershipAccess: (orgId: string, membershipId: string) =>
    adminApi.get<{
      data: {
        membershipId: string;
        userId: string;
        displayName: string;
        email: string;
        membershipStatus: string;
        userStatus: string;
        isCanonicalOwner: boolean;
        ownerMembershipId: string | null;
        roles: Array<{ id: string; key: string; name: string; isSystem: boolean }>;
        permissions: string[];
        accessSummary: {
          posAccess: boolean;
          accountingAccess: boolean;
          teamManagement: boolean;
          roleSources: string[];
        };
      };
    }>(`/admin/v1/orgs/${orgId}/members/${membershipId}/access`),
  assistMembershipRoles: (
    orgId: string,
    membershipId: string,
    body: { roleIds: string[]; reason: string; caseRef?: string },
  ) =>
    adminApi.put<{
      data: {
        membershipId: string;
        beforeRoleKeys: string[];
        afterRoleKeys: string[];
        ownerMembershipId: string | null;
        access: {
          roles: Array<{ id: string; key: string; name: string; isSystem: boolean }>;
          accessSummary: {
            posAccess: boolean;
            accountingAccess: boolean;
            teamManagement: boolean;
            roleSources: string[];
          };
        };
      };
    }>(`/admin/v1/orgs/${orgId}/members/${membershipId}/roles`, body),
};

export const diagnosticsApi = {
  summary: (orgId: string) =>
    adminApi.get<{ data: Record<string, unknown> & {
      sales: { open: number; completed24h: number; voided24h: number; recent: Array<{
        id: string; displayNumber: number; status: string; total: string; currencyCode: string; completedAt: string | null;
      }> };
      inventory: { items: number; movements24h: number; negativeBalances: number };
      accounting: { posted24h: number; draft24h: number };
      sync: { serverRevision: string; latestChangeRevision: string | null; latestChangeAt: string | null; rejectedOrConflict24h: number };
      anomalies: Array<{ code: string; severity: string; message: string }>;
    } }>(`/admin/v1/orgs/${orgId}/diagnostics`),
  sales: (orgId: string, params?: { q?: string; limit?: number }) =>
    adminApi.get<{ data: { sales: Array<{ id: string; displayNumber: number; status: string; total: string; currencyCode: string }> } }>(
      `/admin/v1/orgs/${orgId}/diagnostics/sales${qs(params as Record<string, string> | undefined)}`,
    ),
  saleTrace: (orgId: string, saleId: string) =>
    adminApi.get<{ data: {
      sale: {
        id: string; displayNumber: number; status: string; total: string; currencyCode: string;
        idempotencyKey: string | null;
        till: { name: string; code: string };
        lines: Array<{ id: string; lineKind: string; nameSnapshot: string; quantity: string; lineTotal: string }>;
      };
      steps: Array<{ key: string; label: string; ok: boolean; detail: string }>;
      anomalies: Array<{ code: string; message: string }>;
    } }>(`/admin/v1/orgs/${orgId}/diagnostics/sales/${saleId}`),
  inventoryItems: (orgId: string, params?: { q?: string }) =>
    adminApi.get<{ data: { items: Array<{ id: string; name: string; sku: string | null; status: string; quantityTracked: boolean }> } }>(
      `/admin/v1/orgs/${orgId}/diagnostics/inventory${qs(params as Record<string, string> | undefined)}`,
    ),
  inventoryItem: (orgId: string, itemId: string) =>
    adminApi.get<{ data: {
      item: { name: string; quantityTracked: boolean };
      balances: Array<{ id: string; locationName: string; quantity: string }>;
      movements: Array<{ id: string; type: string; quantity: string; sourceType: string | null; sourceId: string | null; createdAt: string }>;
    } }>(`/admin/v1/orgs/${orgId}/diagnostics/inventory/items/${itemId}`),
  journals: (orgId: string, params?: { q?: string }) =>
    adminApi.get<{ data: { journals: Array<{ id: string; status: string; description: string; sourceType: string | null }> } }>(
      `/admin/v1/orgs/${orgId}/diagnostics/accounting${qs(params as Record<string, string> | undefined)}`,
    ),
  journalTrace: (orgId: string, journalId: string) =>
    adminApi.get<{ data: {
      journal: {
        id: string; balanced: boolean; debitTotal: string; creditTotal: string;
        sourceType: string | null; sourceId: string | null;
        lines: Array<{ id: string; accountCode: string; accountName: string; debit: string; credit: string }>;
      };
    } }>(`/admin/v1/orgs/${orgId}/diagnostics/accounting/journals/${journalId}`),
  procurement: (orgId: string, params?: { q?: string }) =>
    adminApi.get<{ data: { purchaseOrders: Array<{ id: string; number: string; status: string; supplierName: string }> } }>(
      `/admin/v1/orgs/${orgId}/diagnostics/procurement${qs(params as Record<string, string> | undefined)}`,
    ),
  procurementTrace: (orgId: string, purchaseOrderId: string) =>
    adminApi.get<{ data: {
      purchaseOrder: { number: string; status: string };
      receipts: unknown[];
      inventoryMovements: unknown[];
      bills: unknown[];
      anomalies: Array<{ code: string; message: string }>;
    } }>(`/admin/v1/orgs/${orgId}/diagnostics/procurement/${purchaseOrderId}`),
  registers: (orgId: string) =>
    adminApi.get<{ data: { tills: Array<{
      id: string; name: string; code: string; status: string; saleCount: number;
      openSession: { id: string; openedAt: string; staleOpen: boolean } | null;
    }> } }>(`/admin/v1/orgs/${orgId}/diagnostics/registers`),
  sync: (orgId: string) =>
    adminApi.get<{ data: {
      note: string;
      serverRevision: string;
      latestChangeRevision: string | null;
      latestChangeAt: string | null;
      recentMutations: Array<{ id: string; mutationId: string; status: string; collection: string; errorCode: string | null }>;
      authDevices: Array<{ id: string; name: string; platform: string | null; lastActiveAt: string; userId: string }>;
    } }>(`/admin/v1/orgs/${orgId}/diagnostics/sync`),
  mutation: (orgId: string, mutationId: string) =>
    adminApi.get<{ data: {
      mutation: {
        mutationId: string; status: string; collection: string; entityId: string | null;
        errorCode: string | null; errorMessage: string | null;
      };
      changes: Array<{ id: string; revision: string; collection: string; entityId: string; operation: string }>;
    } }>(`/admin/v1/orgs/${orgId}/diagnostics/sync/mutations/${mutationId}`),
};

export const usersApi = {
  list: (params?: Record<string, string>) =>
    adminApi.get<{ data: { users: UserSummary[]; nextCursor: string | null } }>(`/admin/v1/users${qs(params)}`),
  detail: (id: string) =>
    adminApi.get<{ data: { user: UserSummary; sessions: Array<{
      id: string; active: boolean; lastUsedAt: string; userAgent: string | null; expiresAt: string;
    }> } }>(`/admin/v1/users/${id}`),
  terminateSessions: (id: string, reason: string) =>
    adminApi.post<{ data: { revokedCount: number } }>(`/admin/v1/users/${id}/terminate-sessions`, { reason }),
  resendVerification: (id: string, reason: string) =>
    adminApi.post<{ data: { sent: boolean } }>(`/admin/v1/users/${id}/resend-verification`, { reason }),
  requestPasswordReset: (id: string, reason: string) =>
    adminApi.post<{ data: { sent: boolean } }>(`/admin/v1/users/${id}/request-password-reset`, { reason }),
  provisionOrganization: (id: string, reason: string) =>
    adminApi.post<{ data: { organization: { id: string; name: string } } }>(
      `/admin/v1/users/${id}/provision-organization`,
      { reason },
    ),
};

export const analyticsApi = {
  overview: () =>
    adminApi.get<{ data: { summary: { total: number; active: number; suspended: number; archived: number; trialing: number }; recentActivity: Array<{
      id: string; action: string; outcome: string; memo: string | null; createdAt: string;
      administrator: { id: string; name: string } | null;
      organization: { id: string; name: string } | null;
    }> } }>('/admin/v1/analytics/overview'),
  signups: (groupBy?: string) =>
    adminApi.get<{ data: { trend: Array<{ date: string; count: number }> } }>(`/admin/v1/analytics/signups${qs(groupBy ? { groupBy } : undefined)}`),
  revenue: () =>
    adminApi.get<{ data: { mrr: number; arr: number; payingCustomers: number; trialingCustomers: number; primaryCurrency: string } }>(
      '/admin/v1/analytics/revenue',
    ),
};

export const billingApi = {
  summary: () =>
    adminApi.get<{
      data: {
        total: number;
        trialing: number;
        active: number;
        pastDue: number;
        cancelled: number;
        paymentRail?: {
          defaultProvider: 'paystack' | 'flutterwave' | null;
          paystackConfigured: boolean;
          paystackEnvironment: 'sandbox' | 'production' | null;
          flutterwaveConfigured: boolean;
          flutterwaveEnvironment: 'sandbox' | 'production' | null;
        };
      };
    }>('/admin/v1/billing/summary'),
  catalog: () =>
    adminApi.get<{ data: { plans: Array<{
      key: string;
      name: string;
      description: string | null;
      contactSales: boolean;
      prices: Array<{ key: string; currencyCode: string; interval: string; amount: string }>;
    }> } }>('/admin/v1/billing/catalog'),
  plans: () =>
    adminApi.get<{ data: { plans: Array<{ id: string; key: string; name: string }>; prices: Array<{
      id: string; key: string | null; planId: string; currencyCode: string; interval: string; amount: string;
    }> } }>('/admin/v1/billing/plans'),
  listSubscriptions: (params?: Record<string, string>) =>
    adminApi.get<{ data: { subscriptions: BillingSubscription[]; nextCursor: string | null } }>(`/admin/v1/billing/subscriptions${qs(params)}`),
  assignPlan: (orgId: string, body: { planId: string; priceId?: string; reason: string; password: string; trialDays?: number }) =>
    adminApi.post<{ data: { subscription: unknown } }>(`/admin/v1/orgs/${orgId}/billing/assign-plan`, body),
  cancelSubscription: (orgId: string, body: { reason: string; password: string }) =>
    adminApi.post<{ data: { subscription: unknown } }>(`/admin/v1/orgs/${orgId}/billing/cancel-subscription`, body),
  refund: (orgId: string, body: { invoiceId: string; amount?: string; reason: string; password: string }) =>
    adminApi.post<{ data: unknown }>(`/admin/v1/orgs/${orgId}/billing/refund`, body),
  credit: (orgId: string, body: { amount: string; currencyCode: string; reason: string; password: string }) =>
    adminApi.post<{ data: unknown }>(`/admin/v1/orgs/${orgId}/billing/credit`, body),
  checkoutLink: (orgId: string, body: { invoiceId?: string; password: string }) =>
    adminApi.post<{ data: { checkoutUrl: string | null; invoice: { id: string; number: string } } }>(
      `/admin/v1/orgs/${orgId}/billing/checkout-link`,
      body,
    ),
  createCatalogProduct: (body: { key: string; name: string; description?: string; kind: string; capabilityKey: string }) =>
    adminApi.post('/admin/v1/billing/catalog/products', body),
  createCatalogPlan: (body: { key: string; name: string; description?: string; items: Array<{ catalogProductId: string; inclusion?: string; quantity?: number }> }) =>
    adminApi.post('/admin/v1/billing/catalog/plans', body),
  createCatalogPrice: (body: { planId: string; currencyCode: string; interval: string; amount: string; nickname?: string; key?: string }) =>
    adminApi.post('/admin/v1/billing/catalog/prices', body),
};

export const announcementsApi = {
  list: () =>
    adminApi.get<{ data: { announcements: Announcement[] } }>('/admin/v1/announcements'),
  create: (body: Partial<Announcement>) =>
    adminApi.post<{ data: Announcement }>('/admin/v1/announcements', body),
  update: (id: string, body: Partial<Announcement>) =>
    adminApi.patch<{ data: Announcement }>(`/admin/v1/announcements/${id}`, body),
  deactivate: (id: string) =>
    adminApi.delete<{ data: { deactivatedAt: string } }>(`/admin/v1/announcements/${id}`),
};

export const auditApi = {
  list: (params?: Record<string, string>) =>
    adminApi.get<{ data: { records: AuditRecord[]; nextCursor: string | null } }>(`/admin/v1/backoffice-audit${qs(params)}`),
  detail: (id: string) =>
    adminApi.get<{ data: AuditRecordDetail }>(`/admin/v1/backoffice-audit/${id}`),
};

export const teamApi = {
  list: () =>
    adminApi.get<{ data: { administrators: TeamMember[] } }>('/admin/v1/team'),
  create: (body: { email: string; password: string; displayName?: string; role: string }) =>
    adminApi.post<{ data: TeamMember }>('/admin/v1/team', body),
  update: (id: string, body: { role?: string; status?: string }) =>
    adminApi.patch<{ data: TeamMember }>(`/admin/v1/team/${id}`, body),
};

export const authApi = {
  me: () =>
    adminApi.get<{
      administrator: {
        id: string;
        email: string;
        displayName: string;
        role: string;
        mfaEnabled: boolean;
      };
      permissions: string[];
      deploymentEnvironment?: 'STAGING' | 'PRODUCTION';
    }>('/admin/v1/auth/me'),
  sessions: () =>
    adminApi.get<{ sessions: AdminSession[] }>('/admin/v1/auth/sessions'),
  logoutOthers: () =>
    adminApi.post<{ revokedCount: number }>('/admin/v1/auth/logout-others'),
};

export const integrationsApi = {
  list: (params?: Record<string, string>) =>
    adminApi.get<{ data: { connections: IntegrationConnection[]; nextCursor: string | null } }>(`/admin/v1/integrations${qs(params)}`),
  stats: () =>
    adminApi.get<{ data: { total: number; byStatus: Record<string, number>; byProvider: Record<string, number>; recentFailures: number } }>('/admin/v1/integrations/stats'),
  disconnect: (id: string, reason: string) =>
    adminApi.post(`/admin/v1/integrations/${id}/disconnect`, { reason }),
  markReauth: (id: string, reason: string) =>
    adminApi.post(`/admin/v1/integrations/${id}/reauth`, { reason }),
};

export const importsApi = {
  list: (params?: Record<string, string>) =>
    adminApi.get<{ data: { imports: ImportRecord[]; nextCursor: string | null } }>(`/admin/v1/imports${qs(params)}`),
  stats: () =>
    adminApi.get<{
      data: {
        total: number;
        byStatus: Record<string, number>;
        failedLast24h: number;
        bridgeTotal?: number;
        bridgeFailedLast24h?: number;
      };
    }>('/admin/v1/imports/stats'),
  bridgeSessions: (params?: Record<string, string>) =>
    adminApi.get<{ data: { sessions: BridgeSessionRecord[]; nextCursor: string | null } }>(
      `/admin/v1/imports/bridge/sessions${qs(params)}`,
    ),
  bridgeSession: (id: string) =>
    adminApi.get<{
      data: {
        session: BridgeSessionRecord;
        failedRows: Array<{
          datasetName: string;
          rowNumber: number | null;
          field: string | null;
          errorCode: string;
          reason: string;
          level: string | null;
        }>;
        commitFailures: Array<{
          datasetName: string;
          sourceRow: number;
          status: string;
          errorMessage: string | null;
          idempotencyKey: string;
        }>;
      };
    }>(`/admin/v1/imports/bridge/sessions/${id}`),
};

export const notificationsApi = {
  health: () =>
    adminApi.get<{ data: NotificationHealth }>('/admin/v1/notifications/health'),
  emailDeliveries: (params?: Record<string, string>) =>
    adminApi.get<{ data: { deliveries: EmailDeliveryRecord[]; nextCursor: string | null } }>(
      `/admin/v1/notifications/email-deliveries${qs(params)}`,
    ),
  emailDelivery: (id: string) =>
    adminApi.get<{ data: EmailDeliveryRecord }>(`/admin/v1/notifications/email-deliveries/${id}`),
  requeueEmail: (id: string, reason: string) =>
    adminApi.post<{ data: EmailDeliveryRecord & { requeued: boolean; alreadyDue: boolean } }>(
      `/admin/v1/notifications/email-deliveries/${id}/requeue`,
      { reason },
    ),
};

export const webhooksApi = {
  deliveries: (params?: Record<string, string>) =>
    adminApi.get<{ data: { deliveries: WebhookDeliveryRecord[]; nextCursor: string | null } }>(
      `/admin/v1/webhooks/deliveries${qs(params)}`,
    ),
  delivery: (id: string) =>
    adminApi.get<{ data: WebhookDeliveryRecord }>(`/admin/v1/webhooks/deliveries/${id}`),
  stats: () =>
    adminApi.get<{
      data: { total: number; byStatus: Record<string, number>; exhausted: number; failedLast24h: number };
    }>('/admin/v1/webhooks/deliveries/stats'),
  replay: (id: string, reason: string, confirmSideEffects: boolean) =>
    adminApi.post<{ data: WebhookDeliveryRecord }>(`/admin/v1/webhooks/deliveries/${id}/replay`, {
      reason,
      confirmSideEffects,
    }),
};

export const filesApi = {
  list: (params?: Record<string, string>) =>
    adminApi.get<{ data: { files: BackofficeFileRecord[]; nextCursor: string | null } }>(`/admin/v1/files${qs(params)}`),
  download: (id: string) => adminApi.download(`/admin/v1/files/${id}/download`),
};

export type PlatformModule = {
  key: string;
  name: string;
  description: string;
  status: string;
  type: string;
};

export type PlatformSetting = {
  key: string;
  value: unknown;
};

export const modulesApi = {
  list: () => adminApi.get<{ data: PlatformModule[] }>('/admin/v1/modules'),
};

export const platformSettingsApi = {
  list: () => adminApi.get<{ data: Array<{ key: string; value: unknown; description?: string; source?: string }> }>('/admin/v1/settings'),
  put: (key: string, value: unknown) =>
    adminApi.put<{ setting: { key: string; value: unknown } }>(`/admin/v1/settings/${key}`, { value }),
};

export const operationsApi = {
  health: () =>
    adminApi.get<{ data: OperationsHealth }>('/admin/v1/operations/health'),
  signals: () =>
    adminApi.get<{ data: NonNullable<OperationsHealth['asyncWork']> }>('/admin/v1/operations/signals'),
  recoveryRunbook: () =>
    adminApi.get<{
      data: {
        entries: Array<{
          action: string;
          where: string;
          risk: string;
          availableInBackroom: boolean;
          notes: string;
        }>;
        backupVisibility: string;
      };
    }>('/admin/v1/operations/recovery-runbook'),
  cases: (params?: Record<string, string>) =>
    adminApi.get<{
      data: {
        cases: Array<{
          id: string;
          organizationId: string;
          organizationName: string;
          title: string;
          status: string;
          ticketRef: string | null;
          updatedAt: string;
        }>;
      };
    }>(`/admin/v1/operations/cases${qs(params)}`),
  productHealth: (organizationId?: string) =>
    adminApi.get<{ data: Record<string, unknown> }>(`/admin/v1/operations/products/health${qs(organizationId ? { organizationId } : undefined)}`),
  events: (status?: string) =>
    adminApi.get<{
      data: Array<{
        id: string;
        type: string;
        status: string;
        attempts: number;
        lastError: string | null;
        organizationId: string | null;
        organizationName: string | null;
        occurredAt: string;
        publishedAt: string | null;
        ageMs?: number;
        ageLabel?: string;
        factualState?: string;
        stuck?: boolean;
        stuckNote?: string | null;
      }>;
    }>(`/admin/v1/operations/events${qs(status ? { status } : undefined)}`),
  retryEvent: (id: string, reason?: string) =>
    adminApi.post(`/admin/v1/operations/events/${id}/retry`, { reason: reason ?? 'Operator retry' }),
  productSurfaces: (organizationId?: string) =>
    adminApi.get<{ data: { surfaces: Array<{ productKey: string; label: string; health: unknown; actions: Array<{ key: string; label: string }> }> } }>(
      `/admin/v1/operations/products/surfaces${qs(organizationId ? { organizationId } : undefined)}`,
    ),
  runProductAction: (body: { productKey: string; actionKey: string; organizationId?: string }) =>
    adminApi.post('/admin/v1/operations/products/actions', body),
};

export const flagsApi = {
  list: () =>
    adminApi.get<{ data: { flags: Array<{ id: string; key: string; description: string; rollout: string; overrides: Array<{ organizationId: string; organizationName: string; enabled: boolean }> }> } }>('/admin/v1/flags'),
  setRollout: (key: string, rollout: string) =>
    adminApi.patch(`/admin/v1/flags/${encodeURIComponent(key)}`, { rollout }),
  setOverride: (key: string, organizationId: string, enabled: boolean) =>
    adminApi.put(`/admin/v1/flags/${encodeURIComponent(key)}/overrides/${organizationId}`, { enabled }),
};

export const impersonationApi = {
  start: (orgId: string, reason: string, targetMembershipId: string, ticketId?: string) =>
    adminApi.post<{ data: { impersonationKey: string; expiresAt: string; orgName: string; targetUserId: string } }>(
      `/admin/v1/impersonate/start/${orgId}`,
      { reason, targetMembershipId, ticketId },
    ),
  end: (impersonationKey: string) =>
    adminApi.post<{ data: { ended: boolean } }>('/admin/v1/impersonate/end', { impersonationKey }),
  listActive: (orgId: string) =>
    adminApi.get<{ data: { sessions: Array<{
      impersonationKey: string;
      reason: string;
      startedAt: string;
      expiresAt: string;
      administratorId: string;
      targetUserId: string | null;
    }> } }>(`/admin/v1/impersonate/active/${orgId}`),
};

export type WorkItem = {
  id: string;
  signalKey: string;
  kind: string;
  title: string;
  href: string;
  status: 'OPEN' | 'WAITING' | 'RESOLVED';
  organizationId: string | null;
  caseId?: string | null;
  organizationName: string | null;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
};

export const workApi = {
  list: (status?: string) =>
    adminApi.get<{ data: { items: WorkItem[]; counts: { all: number; OPEN: number; WAITING: number; RESOLVED: number } } }>(
      `/admin/v1/work${qs(status ? { status } : undefined)}`,
    ),
  setStatus: (id: string, status: 'OPEN' | 'WAITING' | 'RESOLVED') =>
    adminApi.patch<{ data: WorkItem }>(`/admin/v1/work/${id}`, { status }),
  openCase: (id: string) =>
    adminApi.post<{ data: { id: string; title: string } }>(`/admin/v1/work/${id}/case`, {}),
};

export type CoreAuditRecord = {
  id: string;
  occurredAt: string;
  createdAt: string;
  actorType: string;
  actorId: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  organizationId: string | null;
  organizationName?: string | null;
  outcome: string;
  source: string;
};

export const coreAuditApi = {
  list: (params?: Record<string, string>) =>
    adminApi.get<{ data: CoreAuditRecord[]; page: { nextCursor: string | null; limit: number } }>(`/admin/v1/audit${qs(params)}`),
};
